/**
 * Gallery discovery sidebar vocabulary (DESIGN.md §GalleryDiscovery).
 *
 * The sidebar maps a handful of curated Store-taxonomy (category,
 * subcategory) pairs onto the existing GET /v1/capsules filters — it adds no
 * new API categories, it only connects the taxonomy the API already speaks to
 * the gallery UI. The full 12-category vocabulary stays available through the
 * "All apps" surface; the facets below are the subset surfaced in the
 * sidebar's Explore section.
 */
export type DiscoveryFacet = {
  /** Stable UI id (also used as the URL `category` value when present). */
  id: string;
  label: string;
  category: string;
  subcategory?: string;
};

export const DISCOVERY_FACETS: DiscoveryFacet[] = [
  {
    id: "games",
    label: "Games",
    category: "creative_media",
    subcategory: "three_d_game",
  },
  {
    id: "creative",
    label: "Creative",
    category: "creative_media",
    subcategory: "design",
  },
  {
    id: "ai",
    label: "AI",
    category: "ai_agents",
  },
  {
    id: "productivity",
    label: "Productivity",
    category: "productivity",
  },
  {
    id: "developer",
    label: "Developer",
    category: "developer_tools",
  },
  {
    id: "education",
    label: "Education",
    category: "education_research",
  },
];

const KNOWN_CATEGORY_IDS = new Set(DISCOVERY_FACETS.map((f) => f.category));

/**
 * Sanitize category/subcategory read from a URL (or a persisted param) before
 * they enter SearchProvider state. An unknown/typo'd category is dropped and
 * the filter falls back to the Recommended feed (empty category) — never
 * forwarded to the API, which would only 404 or return an empty grid.
 */
export function sanitizeDiscoveryParams(
  category: string,
  subcategory: string,
): { category: string; subcategory: string } {
  if (!category || !KNOWN_CATEGORY_IDS.has(category)) {
    return { category: "", subcategory: "" };
  }
  return { category, subcategory };
}
